/**
 * Represents a discriminated union of a successful result or a failure.
 * Check `result.ok` to narrow the type.
 *
 * @template T - The type of the successful data.
 * @template E - The type of the error (defaults to Error).
 */
export type Result<T, E = Error> = { ok: true; data: T } | { ok: false; error: E };

/**
 * Interface for the safe error handling utility.
 * It can be called directly as a function for Promises, or via methods for other patterns.
 */
interface TryCatch {
  /**
   * Wraps a Promise, catching any rejections and returning a Result object.
   *
   * @template T - The type of the resolved data.
   * @template E - The type of the error.
   * @param promise - The promise to await.
   * @returns A Promise resolving to a Result containing either data or an error.
   *
   * @example
   * const result = await tryCatch(fetchUser(1));
   * if (!result.ok) {
   *   // Handle error
   *   console.error(result.error);
   *   return;
   * }
   * // Use the successful data
   * console.log(result.data);
   */
  <T, E = Error>(promise: Promise<T>): Promise<Result<T, E>>;

  /**
   * Executes a synchronous function safely, catching any thrown exceptions.
   *
   * @template T - The return type of the function.
   * @template E - The type of the error.
   * @param fn - The synchronous function to execute.
   * @returns A Result object containing either the return value or the caught error.
   *
   * @example
   * const result = tryCatch.sync(() => JSON.parse(userInput));
   */
  sync: <T, E = Error>(fn: () => T) => Result<T, E>;

  /**
   * A safe alternative to `Promise.all`.
   * It awaits multiple promises and returns an array of Results.
   * Unlike `Promise.all`, this will not throw if one promise fails; it returns a failure Result for that specific item.
   *
   * @template T - An array of types corresponding to the promises.
   * @template E - The type of the error.
   * @param promises - A list of promises to await.
   * @returns A Promise resolving to a tuple of Results matching the input order.
   *
   * @example
   * const [res1, res2] = await tryCatch.all(promiseA, promiseB);
   */
  all: <T extends any[], E = Error>(
    ...promises: { [K in keyof T]: Promise<T[K]> }
  ) => Promise<{ [K in keyof T]: Result<T[K], E> }>;

  /**
   * Unwraps a Result object.
   * If the result is successful, it returns the data.
   * If the result is a failure, it throws the error.
   *
   * @template T - The type of the data.
   * @template E - The type of the error.
   * @param result - The Result object to unwrap.
   * @returns The successful data.
   * @throws The error contained in the Result if ok is false.
   *
   * @example
   *
   * const data = tryCatch.unwrap(result);
   * --> // If result.ok is false, this will throw result.error
   */
  unwrap: <T, E>(result: Result<T, E>) => T;
}

const tryCatchBase = async <T, E = Error>(promise: Promise<T>): Promise<Result<T, E>> => {
  try {
    const data = await promise;
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error: error as E };
  }
};

const tryCatchImpl = tryCatchBase as TryCatch;

tryCatchImpl.sync = <T, E = Error>(fn: () => T): Result<T, E> => {
  try {
    return { ok: true, data: fn() };
  } catch (error) {
    return { ok: false, error: error as E };
  }
};

tryCatchImpl.unwrap = <T, E>(result: Result<T, E>): T => {
  if (result.ok === false) {
    throw result.error;
  }
  return result.data;
};

tryCatchImpl.all = async <T extends any[], E = Error>(
  ...promises: { [K in keyof T]: Promise<T[K]> }
): Promise<{ [K in keyof T]: Result<T[K], E> }> => {
  const results = await Promise.all(
    promises.map(async (p) => {
      try {
        const data = await p;
        return { ok: true, data };
      } catch (error) {
        return { ok: false, error: error as E };
      }
    })
  );

  return results as { [K in keyof T]: Result<T[K], E> };
};

/**
 * Utility for handling errors in a functional style (Result pattern).
 * Wraps promises or functions to return `{ ok: true, data }` or `{ ok: false, error }`.
 * Also provides methods for synchronous functions, unwrapping results, and handling multiple promises.
 *
 * @example
 * // Using with a Promise
 * const result = await tryCatch(fetchData());
 *
 * // Using with a synchronous function
 * const result = tryCatch.sync(() => fetchData());
 *
 * // Using with multiple promises
 * const [res1, res2] = await tryCatch.all(promiseA, promiseB);
 */
export const tryCatch = tryCatchImpl;

// ============================================================================
// tRPC Error Types and Utilities
// ============================================================================

/**
 * Represents the custom error data shape added by our tRPC errorFormatter.
 * This is the structure returned in `error.data` for tRPC errors.
 *
 * @see libs/lib-backend-auth/src/trpc/trpc.ts - errorFormatter
 */
export type TrpcErrorData = {
  code: string;
  httpStatus: number;
  path?: string;
  zodError?: unknown;
  requestId?: string;
};

/**
 * Represents a tRPC error with our custom error data.
 * Use this type when working with errors from tRPC calls wrapped in tryCatch.
 */
export type TrpcError = Error & {
  data?: TrpcErrorData;
};

/**
 * Extracts the requestId from a tRPC error.
 * Use this in service files when calling errorDialogPromise.
 *
 * @param error - The error object, typically from a failed tryCatch result
 * @returns The requestId string if present, otherwise undefined
 *
 * @example
 * const result = await tryCatch(api.surveys.getMySurveys.query());
 * if (!result.ok) {
 *   this.orgbrainDialog.errorDialogPromise({ requestId: getRequestId(result.error) });
 *   return [];
 * }
 */
export const getRequestId = (error: unknown): string | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'requestId' in error.data
  ) {
    return (error.data as TrpcErrorData).requestId;
  }
  return undefined;
};
